import { Button, LoadingSpinner, TextLink, Typography } from '@neo4j-ndl/react';
import { CommandLineIconOutline, ScienceMoleculeIcon } from '@neo4j-ndl/react/icons';
import { PLG_EVENTS } from '@nx/analytics-service';
import { APP_SCOPE } from '@nx/constants';
import { useActiveOrg, useAuraUser, useCurrentGuide, useGuidesActions, useTrackEvent } from '@nx/state';
import { useManifest, usePlaylist } from '@nx/ui';
import classNames from 'classnames';
import React from 'react';

import dotNetIcon from '../assets/dotnet.svg';
import goIcon from '../assets/go.svg';
import graphQlIcon from '../assets/graphql.svg';
import javaIcon from '../assets/java.svg';
import javaScriptIcon from '../assets/javascript.svg';
import pythonIcon from '../assets/python.svg';
import queryApiIcon from '../assets/query-api.svg';

const PLAYLIST_NAME = 'developer-hub';
const ICON_SIZE = 25;

const componentForSvg = (svg: string) => {
  const Component = ({ ...props }: React.ImgHTMLAttributes<HTMLImageElement>) => (
    <img draggable={false} src={svg} {...props} />
  );
  return Component;
};

interface IconComponentProps {
  height?: string | number;
  width?: string | number;
}

const fixNdlIconSize = (Component: React.FC<React.SVGProps<SVGSVGElement>>): React.FC<IconComponentProps> => {
  const ComponentToReturn = ({ height, width }: IconComponentProps) => (
    <Component height={height} width={width} style={{ height: `${height}px`, width: `${width}px` }} />
  );
  return ComponentToReturn;
};

const connectionMethodIconComponents: Record<string, React.FC<IconComponentProps>> = {
  'developer-hub-python': componentForSvg(pythonIcon),
  'developer-hub-java': componentForSvg(javaIcon),
  'developer-hub-javascript': componentForSvg(javaScriptIcon),
  'developer-hub-go': componentForSvg(goIcon),
  'developer-hub-dotnet': componentForSvg(dotNetIcon),
  'developer-hub-graphql': componentForSvg(graphQlIcon),
  'developer-hub-query-api': componentForSvg(queryApiIcon),
  'developer-hub-cli': fixNdlIconSize(CommandLineIconOutline),
  'developer-hub-graph-analytics': fixNdlIconSize(ScienceMoleculeIcon),
};

type Option = {
  label: string;
  value: string;
};

const ConnectionMethodButton = ({
  option,
  selected,
  onClick,
}: {
  option: Option;
  selected: boolean;
  onClick: (option: Option) => void;
}) => {
  const className = classNames(
    'flex-[1_0_0] items-center justify-start gap-3 rounded-lg !p-3 box-content',
    'min-w-[184.5px] max-w-[184.5px] !h-[22px]',
    {
      'border-neutral-border-weak m-px': !selected,
      'border-primary-border-strong !border-2': selected,
    },
  );
  const IconComponent = connectionMethodIconComponents[option.value];
  return (
    <Button className={className} color="neutral" fill="outlined" onClick={() => onClick(option)}>
      {IconComponent && <IconComponent height={ICON_SIZE} width={ICON_SIZE} />}
      <Typography variant="subheading-medium" className="text-neutral-text-default">
        {option.label}
      </Typography>
    </Button>
  );
};

const getOptions = (guides: { title: string; id: string }[]): Option[] =>
  Object.keys(connectionMethodIconComponents).reduce((acc: Option[], connectionMethodId) => {
    const guide = guides.find(({ id }) => id === connectionMethodId);
    if (guide !== undefined) {
      acc.push({ label: guide.title, value: guide.id });
    }
    return acc;
  }, []);

const ConnectionMethodSelector = () => {
  const { selectGuide } = useGuidesActions();
  const currentGuide = useCurrentGuide();
  const playlist = usePlaylist();
  const manifest = useManifest();
  const track = useTrackEvent();
  const org = useActiveOrg();
  const { user } = useAuraUser();

  if (manifest.data === undefined) {
    if (manifest.isFetching || manifest.isLoading) {
      return <LoadingSpinner size="large" />;
    }
    return null;
  }

  if (playlist.id !== PLAYLIST_NAME) {
    if (!(PLAYLIST_NAME in manifest.data.playlists)) {
      return null;
    }
    playlist.changePlaylist(PLAYLIST_NAME);
  }

  if (playlist.data === undefined) {
    if (playlist.isFetching) {
      return <LoadingSpinner size="large" />;
    }
    return null;
  }

  const options = getOptions(playlist.data.steps);
  const [defaultOption] = options;
  if (defaultOption === undefined) {
    return null;
  }

  const handleGuideChange = ({ value: id, label: title }: Option) => {
    const manifestEntry = manifest.data?.guides[id];

    if (manifestEntry !== undefined) {
      void selectGuide({
        id,
        title,
        url: manifestEntry.url,
      });

      track({
        event: PLG_EVENTS.DEVELOPER_HUB_BUTTON,
        scope: APP_SCOPE.aura,
        properties: {
          button: title,
          org_id: org.id,
          user_id: user?.id,
        },
      });
    }
  };

  const currentGuideIsInCurrentPlaylist = playlist.data.steps.some(({ id }) => id === currentGuide.guide?.id);

  if (
    (!currentGuideIsInCurrentPlaylist || currentGuide.guide === undefined) &&
    currentGuide.fetchStatus === 'loading'
  ) {
    return <LoadingSpinner size="large" />;
  }

  if (!currentGuideIsInCurrentPlaylist) {
    handleGuideChange(defaultOption);
  }

  return (
    <div className="flex flex-col gap-4">
      <h5>Choose how you want to connect</h5>
      <div className="flex flex-wrap content-center items-center gap-2.5 self-stretch">
        {options.map((option) => (
          <ConnectionMethodButton
            option={option}
            selected={option.value === currentGuide.guide?.id}
            onClick={handleGuideChange}
            key={option.value}
          />
        ))}
      </div>
      <TextLink
        href="https://neo4j.com/deployment-center/#drivers-tab"
        type="external"
        htmlAttributes={{
          onClick: () => {
            track({
              event: PLG_EVENTS.DEVELOPER_HUB_LINK,
              scope: APP_SCOPE.aura,
              properties: {
                link: 'deployment-center',
                org_id: org.id,
                user_id: user?.id,
              },
            });
          },
        }}
      >
        <Typography variant="body-medium" className="tracking-normal">
          See more ways you can connect to your instance
        </Typography>
      </TextLink>
    </div>
  );
};

export default ConnectionMethodSelector;
